import {
    buildInteractiveResult,
    buildRelevanceSortCriterion,
    buildResultList,
    buildResultsPerPage,
    buildSort,
    loadContextActions,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
} from '@coveo/headless';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import PropTypes from 'prop-types';
import React from 'react';

import { GeolocationProvider, useGeolocationManager } from '@/contexts/GeolocationContext';
import { Button } from '@/design-system/atoms/Button';
import { ProviderCarousel as ProviderCarouselUI } from '@/design-system/molecules/ProviderCarousel';
import deriveHasVirtualCare from '@/features/ProviderSearch/components/ProviderResultsList/deriveHasVirtualCare';
import { useObserver } from '@/hooks/useObserver';
import { useScrollToElementInHash } from '@/hooks/useScrollToElementInHash';
import { arrayShuffle } from '@/utils/arrayShuffle';
import {
    formatEasternDate,
    formatEasternTime,
    getEasternDayDiff,
    isWithinOneHour,
} from '@/utils/easternDate';

const AVAILABILITY_SOON_DAYS = 180;
const AVAILABILITY_ONLINE_DAYS = 545;

/**
 * Compute the availability eyebrow text for a provider card.
 * Returns the full human-readable string or null when no message applies.
 */
function computeAvailabilityText({
    availableSoonOnline,
    hasVirtualCare,
    now,
    firstavailabilitydate,
    dayDiff,
}) {
    if (!availableSoonOnline) {
        return null;
    }
    if (hasVirtualCare && isWithinOneHour(now, firstavailabilitydate)) {
        return 'Available Now';
    }
    if (dayDiff === 0) {
        return `Available as soon as: ${formatEasternTime(firstavailabilitydate)}`;
    }
    return `Available as soon as: ${formatEasternDate(firstavailabilitydate)}`;
}

const ProviderCarousel = ({
    componentId,
    htmlId = 'doctors',
    title,
    ctaPreText,
    cta,
    dynamicCta,
    coveo,
    coveoQuery,
    randomized,
    isDynamic,
    primaryLocationId,
    primaryLocationLatitude,
    primaryLocationLongitude,
    disableAvailabilityMessage = false,
}) => {
    const { currentGeolocation } = useGeolocationManager();
    const hasCoveoQuery = Boolean(coveoQuery);
    const pageSize = randomized ? 25 : 10;
    const [providers, setProviders] = React.useState([]);
    const [showCta, setShowCta] = React.useState(false);
    const sectionRef = React.useRef(null);
    const { ref, inView } = useObserver({
        triggerOnce: true,
        threshold: 0,
        rootMargin: '400px 0px',
    });

    // Handle scrolling to element when hash matches htmlId
    useScrollToElementInHash(sectionRef, true);

    // Utility to check if in AEM editor
    const isInEditor = () => {
        if (typeof window !== 'undefined') {
            return (
                window.location &&
                (window.location.search.includes('wcmmode=edit') ||
                    window.location.search.includes('wcmmode=preview'))
            );
        }
        return false;
    };

    // Coveo search engine
    const engine = React.useMemo(() => {
        if (coveo && hasCoveoQuery) {
            // Include geolocation in engine name to force cache invalidation when location changes
            const locationKey =
                currentGeolocation?.lat && currentGeolocation?.lng
                    ? `${currentGeolocation.lat}-${currentGeolocation.lng}`
                    : 'no-location';

            return getHeadlessEngine(`provider-carousel-${componentId}-${locationKey}`, {
                ...coveo,
                search: { searchHub: 'FeaturedDoctors' },
                preprocessRequest: (request, _clientOrigin, metadata) => {
                    const hasLocation = Boolean(currentGeolocation?.lat && currentGeolocation?.lng);

                    const lat = hasLocation ? currentGeolocation?.lat : primaryLocationLatitude;
                    const lng = hasLocation ? currentGeolocation?.lng : primaryLocationLongitude;
                    const hasLatLng = Boolean(lat && lng);

                    if (!metadata || metadata.method !== 'search' || randomized || !hasLatLng) {
                        return request;
                    }

                    const body = JSON.parse(request.body);

                    const latLngString = `${lat ?? 0},${lng ?? 0}`;

                    body.queryFunctions = [
                        {
                            fieldName: '@mydistance',
                            function: `min(dist(@loc1latitude, @loc1longitude, ${latLngString}), dist(@loc2latitude, @loc2longitude, ${latLngString}))`,
                        },
                        {
                            fieldName: '@mydistanceinmiles',
                            function: '@mydistance * 0.000621371',
                        },
                    ];

                    request.body = JSON.stringify(body);

                    return request;
                },
                analytics: { analyticsMode: 'legacy', trackingId: 'featured-doctors' },
            });
        }
        return null;
    }, [
        hasCoveoQuery,
        coveo,
        componentId,
        currentGeolocation,
        randomized,
        primaryLocationLatitude,
        primaryLocationLongitude,
    ]);

    const QueryActionCreators = React.useMemo(
        () => (engine ? loadQueryActions(engine) : null),
        [engine],
    );

    const ContextActionCreators = React.useMemo(
        () => (engine ? loadContextActions(engine) : null),
        [engine],
    );

    const SearchAnalyticsActionCreators = React.useMemo(
        () => (engine ? loadSearchAnalyticsActions(engine) : null),
        [engine],
    );

    const SearchActionCreators = React.useMemo(
        () => (engine ? loadSearchActions(engine) : null),
        [engine],
    );

    const controllerResultsList = React.useMemo(() => {
        if (engine) {
            const controllerResultList = buildResultList(engine, {
                options: {
                    fieldsToInclude: [
                        'entitytype',
                        'filetype',
                        'firstname',
                        'lastname',
                        'fullname',
                        'imageurl',
                        'location',
                        'locationcitystate',
                        'locationcity',
                        'locationstate',
                        'locationzip',
                        'locationphone',
                        'latitude',
                        'longitude',
                        'permanentid',
                        'affiliationrank',
                        'providertype',
                        'providernpi',
                        'specialties',
                        'primaryspecialty',
                        'overallrating',
                        'onlinebookingavailable',
                        'specialtyservices',
                        'locaddressoverridemessage',
                        'firstavailabilitydate',
                        'nextavailabilitydate',
                    ],
                },
            });

            // Set the number of results to the page size
            buildResultsPerPage(engine, {
                initialState: { numberOfResults: pageSize },
            });

            // Set the sorting to relevance by default
            buildSort(engine, {
                initialState: {
                    criterion: [buildRelevanceSortCriterion()],
                },
            });

            return controllerResultList;
        }
        return null;
    }, [engine, pageSize]);

    const consolidatedCta = React.useMemo(
        () => ({
            ...dynamicCta,
            ...cta,
            href: cta?.href || dynamicCta?.href,
            label: cta?.label || dynamicCta?.label || 'View All Doctors',
            ariaLabel: cta?.ariaLabel || dynamicCta?.ariaLabel,
        }),
        [cta, dynamicCta],
    );

    /**
     * Execute a coveo search
     * @returns {function} The search action
     */
    const executeSearch = React.useCallback(() => {
        if (SearchActionCreators && SearchAnalyticsActionCreators) {
            return SearchActionCreators.executeSearch(
                SearchAnalyticsActionCreators.logInterfaceLoad(),
            );
        }
        return null;
    }, [SearchActionCreators, SearchAnalyticsActionCreators]);

    /**
     * Trigger a coveo search using the coveoQuery parameter
     * @returns {void}
     */
    const triggerExecuteSearch = React.useCallback(() => {
        if (engine && QueryActionCreators && ContextActionCreators) {
            engine.dispatch(
                QueryActionCreators.updateQuery({
                    enableQuerySyntax: true,
                    q: coveoQuery,
                }),
            );

            const hasLocation = Boolean(currentGeolocation?.lat && currentGeolocation?.lng);
            const context = {
                locationKnown: hasLocation,
                mydistanceKnown: !randomized ? isDynamic || hasLocation : false,
                primaryLocationId: primaryLocationId,
                latitude: hasLocation ? currentGeolocation?.lat : primaryLocationLatitude,
                longitude: hasLocation ? currentGeolocation?.lng : primaryLocationLongitude,
            };

            engine.dispatch(ContextActionCreators.setContext(context));

            engine.dispatch(executeSearch());
        }
    }, [
        engine,
        QueryActionCreators,
        ContextActionCreators,
        isDynamic,
        randomized,
        currentGeolocation,
        coveoQuery,
        primaryLocationId,
        primaryLocationLatitude,
        primaryLocationLongitude,
        executeSearch,
    ]);

    React.useEffect(() => {
        if (hasCoveoQuery && inView) {
            // Trigger coveo provider search
            triggerExecuteSearch();
        }
    }, [hasCoveoQuery, inView, triggerExecuteSearch]);

    React.useEffect(() => {
        if (hasCoveoQuery && controllerResultsList) {
            /**
             * Subscribe to the results list
             * @returns {function} The unsubscribe function
             */
            const unsubscribe = controllerResultsList.subscribe(() => {
                const { results } = controllerResultsList.state;
                if (Array.isArray(results)) {
                    // Total number of providers to show in the carousel
                    const totalNeeded = 3;
                    let slicedResults;

                    setShowCta(results?.length > totalNeeded);

                    if (results.length <= totalNeeded) {
                        // If 3 or fewer results, take them all
                        slicedResults = results;
                    } else if (!randomized && !isDynamic) {
                        // If not randomized, just take the top 3 results
                        slicedResults = results.slice(0, totalNeeded);
                    } else {
                        // Otherwise, randomize results by:
                        // - Grouping by score
                        // - Shuffling within each score group
                        // - Taking from each group until we have 3 results

                        // Create a map to hold groups of results by score.
                        const scoreGroups = new Map();

                        // Group by score
                        // E.g. [[{score: 1}], [{score: 2}], [{score: 3}, {score: 3}]]
                        for (const result of results) {
                            const score = result.score ?? 0;
                            if (!scoreGroups.has(score)) {
                                scoreGroups.set(score, []);
                            }
                            scoreGroups.get(score).push(result);
                        }

                        // Convert to array and sort by score (descending for better results first)
                        // E.g. [[{score: 3}, {score: 3}], [{score: 2}], [{score: 1}]]
                        const sortedGroups = Array.from(scoreGroups.values()).sort(
                            (a, b) => (b[0]?.score || 0) - (a[0]?.score || 0),
                        );

                        slicedResults = [];

                        // Process groups until we have 3 results
                        for (const group of sortedGroups) {
                            // Shuffle the current group
                            const shuffled = arrayShuffle(group);

                            // Determine how many more results we need
                            const needed = totalNeeded - slicedResults.length;
                            slicedResults.push(...shuffled.slice(0, needed));

                            if (slicedResults.length >= totalNeeded) {
                                // Ensure we do not exceed 3 results
                                slicedResults = slicedResults.slice(0, totalNeeded);
                                break;
                            }
                        }
                    }

                    const parsedResults = Array.isArray(slicedResults)
                        ? slicedResults.map((result) => {
                              const interactiveResultController = engine
                                  ? buildInteractiveResult(engine, {
                                        options: { result: result },
                                    })
                                  : null;

                              const { title, clickUri, raw } = result;
                              const {
                                  permanentid,
                                  onlinebookingavailable,
                                  primaryspecialty,
                                  specialties,
                                  imageurl,
                                  overallrating,
                                  locationcitystate,
                                  locationcity,
                                  locationstate,
                                  locationphone,
                                  hospitalaffiliations,
                                  latitude,
                                  longitude,
                                  specialtyservices,
                                  locaddressoverridemessage,
                                  firstavailabilitydate,
                              } = raw;

                              const hasVirtualCare = deriveHasVirtualCare(specialtyservices);
                              const locationAddressOverride = locaddressoverridemessage || null;

                              const now = new Date();
                              const dayDiff = getEasternDayDiff(now, firstavailabilitydate);
                              const availableSoonOnline =
                                  typeof dayDiff === 'number' &&
                                  dayDiff >= 0 &&
                                  dayDiff <= AVAILABILITY_SOON_DAYS;
                              const availableOnline =
                                  typeof dayDiff === 'number' &&
                                  dayDiff >= 0 &&
                                  dayDiff <= AVAILABILITY_ONLINE_DAYS;
                              const availabilityText = computeAvailabilityText({
                                  availableSoonOnline,
                                  hasVirtualCare,
                                  now,
                                  firstavailabilitydate,
                                  dayDiff,
                              });

                              let image;
                              if (imageurl) {
                                  image = {
                                      defaultImgSrc: imageurl,
                                  };
                              }

                              let location = [];
                              if (locationcitystate) {
                                  location = locationcitystate;
                              } else if (
                                  locationcity &&
                                  locationstate &&
                                  Array.isArray(locationcity)
                              ) {
                                  for (let [index, city] of locationcity.entries()) {
                                      const state = locationstate[index];
                                      location.push(`${city}${state ? ', ' + state : ''}`);
                                  }
                              }

                              let hospitalAffiliation;
                              if (
                                  hospitalaffiliations &&
                                  hospitalaffiliations.includes('Atlantic Medical Group')
                              ) {
                                  hospitalAffiliation = 'Atlantic Medical Group';
                              }

                              const link = {
                                  href: new URL(clickUri).pathname,
                                  target: '_self',
                                  csr: true,
                                  ariaLabel: `View profile for ${title}`,
                              };

                              const availability = {
                                  phoneNumber:
                                      locationphone && Array.isArray(locationphone)
                                          ? locationphone[0]
                                          : null,
                                  schedulingCta: {
                                      href: `${new URL(clickUri).pathname}#scheduling`,
                                      target: '_self',
                                      csr: true,
                                      ariaLabel: hasVirtualCare
                                          ? `Schedule Your Virtual Visit with ${title}`
                                          : `View All Times & Schedule with ${title}`,
                                  },
                              };

                              const ignoreSpecialties = ['Primary Care'];

                              const filteredSpecialties = Array.isArray(specialties)
                                  ? specialties.filter(
                                        (specItem) => !ignoreSpecialties.includes(specItem),
                                    )
                                  : [];

                              const item = {
                                  id: permanentid,
                                  title: title.replace(',', ', '),
                                  link,
                                  image,
                                  hasVirtualCare,
                                  locationAddressOverride,
                                  availableOnline,
                                  availabilityText,
                                  showAvailability: !disableAvailabilityMessage,
                                  primarySpecialty: primaryspecialty,
                                  specialties: filteredSpecialties,
                                  hospitalAffiliation,
                                  location,
                                  averageRating: overallrating,
                                  position: {
                                      lat: latitude,
                                      lng: longitude,
                                  },
                                  onlineBookingAvailable: onlinebookingavailable === 'True',
                                  availability,
                                  coveoAnalyticsCallbacks: interactiveResultController
                                      ? {
                                            cancelPendingSelect: () =>
                                                interactiveResultController.cancelPendingSelect(),
                                            onClick: () => interactiveResultController.select(),
                                            onContextMenu: () =>
                                                interactiveResultController.select(),
                                            onMouseDown: () => interactiveResultController.select(),
                                            onMouseUp: () => interactiveResultController.select(),
                                            onTouchStart: () =>
                                                interactiveResultController.beginDelayedSelect(),
                                            onTouchEnd: () =>
                                                interactiveResultController.cancelPendingSelect(),
                                        }
                                      : undefined,
                              };

                              return item;
                          })
                        : [];

                    // Set the parsed providers
                    setProviders(parsedResults || []);
                }
            });

            return () => {
                // Unsubscribe from the results list
                unsubscribe();
            };
        }
    }, [
        controllerResultsList,
        hasCoveoQuery,
        pageSize,
        currentGeolocation,
        randomized,
        isDynamic,
        engine,
        disableAvailabilityMessage,
    ]);

    // Combine useObserver ref and sectionRef for hash scrolling
    const combinedRef = React.useCallback(
        (node) => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
            sectionRef.current = node;
        },
        [ref],
    );

    const shouldRenderCarousel = Boolean(
        hasCoveoQuery || providers.length || showCta || isInEditor(),
    );

    return (
        <div ref={combinedRef} id={htmlId}>
            {shouldRenderCarousel && (
                <ProviderCarouselUI
                    title={title}
                    cta={consolidatedCta}
                    ctaPreText={ctaPreText}
                    showCta={showCta}
                    providers={providers}
                    location={hasCoveoQuery ? currentGeolocation?.recentSearch : null}
                />
            )}
        </div>
    );
};

ProviderCarousel.propTypes = {
    /**
     * component id
     */
    componentId: PropTypes.string,
    /**
     * html id for deep link
     */
    htmlId: PropTypes.string,
    /**
     * title of the component
     */
    title: PropTypes.string.isRequired,
    /**
     * whether the cards are randomized in ordering
     */
    randomized: PropTypes.bool,
    /**
     * CTA Pre-text
     */
    ctaPreText: PropTypes.string,
    /**
     * CTA to overwrite dynamically generated CTA
     */
    cta: PropTypes.shape(Button.propTypes),
    /**
     * dynamically generated CTA. It parameterizes the selected category attribute and appends it to the FAD search URL
     */
    dynamicCta: PropTypes.shape(Button.propTypes),
    /**
     * coveo configuration object
     */
    coveo: PropTypes.shape({
        organizationId: PropTypes.string,
        accessToken: PropTypes.string,
        platformUrl: PropTypes.string,
        renewAccessTokenUrl: PropTypes.string,
    }),
    /**
     * coveo query
     */
    coveoQuery: PropTypes.string,
    primaryLocationId: PropTypes.string,
    primaryLocationLatitude: PropTypes.number,
    primaryLocationLongitude: PropTypes.number,
    /**
     * Feature flag — when true, suppresses all availability messaging on provider cards
     */
    disableAvailabilityMessage: PropTypes.bool,
};

const ProviderCarouselWithGeolocation = (props) => {
    return (
        <GeolocationProvider>
            <ProviderCarousel {...props} />
        </GeolocationProvider>
    );
};

export default ProviderCarouselWithGeolocation;
